import React, { useMemo, useContext } from 'react';
import styled from 'styled-components';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useHelpDeskCategoriesAllBranches from '@ubeya/shared/hooks/account/useHelpDeskCategoriesAllBranches';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import DropDown from '../../DropDown';
import BaseCheckBox from '../../CheckBox';
import FilterLayout from '../FilterLayout';
import ConfigContext from '../../../contexts/ConfigContext';
import useHelpDeskMultiSelectFilter from './useHelpDeskMultiSelectFilter';

const CheckBox = styled(BaseCheckBox)`
  flex: 1;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  margin-top: 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey2};
  &:first-child {
    border-top: none;
  }
`;

const buildInitialMap = (defaultValue) => {
  if (!defaultValue) return {};
  const values = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
  return values.reduce((all, item) => {
    const categoryId = item?.categoryId;
    if (!categoryId) return all;
    return {
      ...all,
      [categoryId]: { categoryId, categoryName: item?.categoryName || '', branchName: item?.branchName || '' }
    };
  }, {});
};

const HelpDeskCategoriesFilter = ({ defaultValue, handleClose, onApply, search, hideLayout = false }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { branches } = useBranches();

  const adminBranches = useMemo(() => branches?.map((b) => ({ id: b.id, name: b.name })) ?? [], [branches]);

  const branchNameById = useMemo(() => Object.fromEntries(adminBranches.map((b) => [b.id, b.name])), [adminBranches]);

  const { categoriesByBranch, isLoading } = useHelpDeskCategoriesAllBranches({
    accountId,
    branches: adminBranches
  });

  const initialMap = useMemo(() => buildInitialMap(defaultValue), [defaultValue]);
  const { selectedMap, handleToggle, applySelection } = useHelpDeskMultiSelectFilter(initialMap, {
    onApply,
    hideLayout,
    handleClose
  });

  const filteredBySearch = useMemo(() => {
    if (!search?.trim()) return categoriesByBranch;
    const lower = search.trim().toLowerCase();
    const out = {};
    Object.entries(categoriesByBranch || {}).forEach(([branchId, categories]) => {
      const matched = (categories || []).filter((c) => c.name?.toLowerCase().includes(lower));
      if (matched.length) out[branchId] = matched;
    });
    return out;
  }, [categoriesByBranch, search]);

  if (isLoading) return null;

  const branchIds = adminBranches.map((b) => b.id);
  const hasAny = branchIds.some((id) => (filteredBySearch[id] || []).length > 0);

  if (!hasAny) return null;

  return (
    <FilterLayout
      name={t('help_desk_categories')}
      handleClose={handleClose}
      onApply={() => applySelection(selectedMap)}
      hideLayout={hideLayout}>
      {branchIds.map((branchId) => {
        const categories = filteredBySearch[branchId] || [];
        const branchName = branchNameById[branchId] || '';
        if (!categories.length) return null;
        return (
          <React.Fragment key={branchId}>
            <SectionTitle>{branchName}</SectionTitle>
            {categories.map((category) => {
              const key = String(category.id);
              return (
                <DropDown.Item key={`${branchId}-${category.id}`}>
                  <CheckBox
                    name={`help-desk-category-${branchId}-${category.id}`}
                    value={!!selectedMap[key]}
                    onChange={() =>
                      handleToggle(key, {
                        categoryId: category.id,
                        categoryName: category.name,
                        branchName: branchName || ''
                      })
                    }
                    label={category.name}
                  />
                </DropDown.Item>
              );
            })}
          </React.Fragment>
        );
      })}
    </FilterLayout>
  );
};

export default HelpDeskCategoriesFilter;
